import React, { Fragment, useEffect } from 'react'
import SelectField from 'components/Common/SelectField'
import Button from 'components/Common/Button'
import { MEETING_STEPPER, availabilityStatus, timeDurationAvailabilityOptions } from 'config'
import { getRoleFromPath, getUserRole } from 'utils/helper'
import { addDays } from 'date-fns'

import { BoAdvisorWrapper, MeetingDuration, MeetingDurationTitle } from 'components/Calendar/style'
import { useFlashParams } from '../../hooks/useFlashParams'
import redirectTo from '../../utils/redirectTo'
import useTenantId from '../../hooks/useTenantId'
import { toNumber } from 'lodash'
import GradientCircleNotchIcon from 'components/Common/SvgIcons/GradientCircleNotchIcon'
import Tile from 'components/Common/Tile'
import { useQueryParams } from 'hooks/useQueryParams'

interface IBsoClientProps {
  onSelect: (schedulingUrl: string) => void
  userAvailabilityList
  setSelectedAdvisorId: (id: string) => void
  updateMeetingStepperAction: (params: { actionType: string; stepName: string }) => void
  getUserAvailabilityAction: (params: {
    role: string[]
    startTime: string
    endTime: string
    participantId: string
    timeZone: string
    duration: number
  }) => void
  userAvailabilityStatus?: string
  list: { firstName: string; lastName: string; id: string; businessName: string; status: string }[]
  isAllClientFetching: boolean
  userProfile: any
  selectedClient: any
  setSelectedClient: (client) => void
  getAdvisorDashboardAction: (payload?: any) => { type: 'GET_ADVISORS_DASHBOARD_STARTED'; payload }
  selectedDuration: number
  setSelectedDuration: (duration: number) => void
  setShowModalBox: (e: boolean) => void
}

export default function BsoClient({
  onSelect,
  userAvailabilityList,
  userAvailabilityStatus,
  updateMeetingStepperAction,
  getUserAvailabilityAction,
  list,
  isAllClientFetching,
  userProfile,
  selectedClient,
  setSelectedClient,
  getAdvisorDashboardAction,
  selectedDuration,
  setSelectedDuration,
  setShowModalBox,
}: IBsoClientProps) {
  const tenantId = useTenantId()
  const [clientId] = useQueryParams(['clientId'])
  const { flashParams, removeFlashParams } = useFlashParams<{
    availabilityConnectionError: string
  }>()

  const currentDate = new Date()
  const startTime = currentDate.toISOString()
  const endTime = addDays(currentDate, 26).toISOString()

  useEffect(() => {
    if (!list?.length) {
      getAdvisorDashboardAction()
    }
  }, [getAdvisorDashboardAction, list?.length])

  if (list?.length === 1 && list[0].status === 'active') {
    setSelectedClient(list[0])
  }

  useEffect(() => {
    removeFlashParams(['availabilityConnectionError'])
    setSelectedDuration(0)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (clientId) {
      const client = list?.find((client) => client.id === clientId)
      client && setSelectedClient(client)
    }
  }, [list, clientId, setSelectedClient])

  const handleChange = (duration: string) => {
    if (toNumber(duration) !== selectedDuration) {
      setSelectedDuration(toNumber(duration))
      getUserAvailabilityAction({
        role: [getUserRole()],
        startTime,
        endTime,
        participantId: selectedClient.id,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        duration: toNumber(duration),
      })
    }
  }

  const handleSelect = (value) => {
    onSelect(value.schedulingUrl)
    setSelectedClient(value)
  }

  const availabilityErrorMessage = `The business owner's calendar is having connection errors. Please message them to schedule a meeting.`
  return (
    <BoAdvisorWrapper>
      <SelectField
        label={'Client'}
        name="clients"
        placeholder="Search"
        options={list
          ?.filter(({ status }) => status === 'active')
          .map((user) => ({ ...user, fullName: `${user.firstName} ${user.lastName}` }))
          .sort((a, b) => a.fullName.localeCompare(b.fullName))
          .map((user) => {
            return {
              label: `${user.fullName} ${user.businessName ? `(${user.businessName})` : ''}`,
              value: user,
            }
          })}
        onChange={handleSelect}
        value={selectedClient}
        isLoading={isAllClientFetching}
      />
      <MeetingDuration>
        {selectedClient && (
          <Fragment>
            <MeetingDurationTitle>Select meeting duration:</MeetingDurationTitle>
            <div className="flex flex-row gap-3 flex-wrap">
              {timeDurationAvailabilityOptions.map((option) => (
                <Tile
                  onChange={handleChange}
                  value={selectedDuration.toString()}
                  label={option.label}
                  optionValue={option.value}
                  className={'w-[127px] !h-[70px]'}
                />
              ))}
            </div>
          </Fragment>
        )}
      </MeetingDuration>
      {/* Loading while getUserAvailability is being called */}
      {selectedClient && userAvailabilityStatus === availabilityStatus.LOADING && (
        <div className="h-full flex flex-col items-center p-5">
          <GradientCircleNotchIcon width="30" height="30" />
        </div>
      )}
      {selectedClient &&
        userAvailabilityStatus === availabilityStatus.FAILED &&
        flashParams.availabilityConnectionError && (
          <Fragment>
            <p className="mt-8">{availabilityErrorMessage}</p>
            <Button
              className="mt-8 w-full"
              variant="primary"
              label="Send a message"
              onClick={() => {
                redirectTo(`/${getRoleFromPath()}/${tenantId}/chat`)
              }}
            />
          </Fragment>
        )}
      {selectedClient && userAvailabilityStatus === availabilityStatus.SUCCESS && (
        <Button
          label={MEETING_STEPPER.CALENDAR}
          onClick={() =>
            updateMeetingStepperAction({
              actionType: 'increment',
              stepName: MEETING_STEPPER.CALENDAR,
            })
          }
          disabled={userAvailabilityList?.availability?.length === 0}
          className="w-full"
        />
      )}
    </BoAdvisorWrapper>
  )
}

BsoClient.defaultProps = {
  list: [],
  isAllClientFetching: false,
  onSelect: () => {},
  boAdvisors: [],
  selectedAdvisorId: '',
  userAvailabilityList: [],
  setSelectedAdvisorId: () => {},
  updateMeetingStepperAction: () => {},
  getUserAvailabilityAction: () => {},
  userAvailabilityStatus: '',
  userProfile: {},
  selectedClient: {},
  setSelectedClient: () => {},
  selectedDuration: 60,
  setSelectedDuration: () => {},
  setShowModalBox: (e: boolean) => {},
}
